import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchPaginated } from '@/lib/supabase-utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmployeeFilterCombobox } from '@/components/shared/employee-filter-combobox';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/providers/auth-provider';
import { useToast } from '@/components/ui/use-toast';
import { format, parseISO } from 'date-fns';
import { getTodayQatar, getNDaysAgoQatar, startOfDayQatar, endOfDayQatar } from '@/utils/timezone';
import { getLastScreenshotByTimeLogId, getReportableTimeLogIntervals, mergeTimeIntervals, type TimeInterval } from '@/lib/time-utils';
import { fetchSessionScreenshotEvidence } from '@/lib/screenshot-evidence';
import { calculatePayableTimeSummary } from '@/lib/payable-time';
import { formatEmployeeId, formatPosition } from '@/lib/employee-display';
import { Calendar, Clock, Download, Filter, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// TimeReports module - optimized for performance

interface TimeReport {
  id: string;
  user_id: string;
  project_id: string | null; // Updated to allow null
  start_time: string;
  end_time: string | null;
  is_idle: boolean;
  idle_seconds?: number;
  user_name?: string;
  user_email?: string;
  user_staff_id?: string | null;
  user_position?: string | null;
  project_name?: string;
  smart_start_ms?: number;
  smart_end_ms?: number; // Smart-capped end time based on screenshots
  status?: string | null;
}

interface User {
  id: string;
  full_name: string;
  email: string;
  staff_id?: string | null;
  position?: string | null;
}

interface Project {
  id: string;
  name: string;
}

// Session lifecycle takes precedence over the idle flag. An ended session is
// completed even when its last activity was idle or its legacy status is stale.
const getReportStatus = (report: TimeReport): 'completed' | 'active' | 'idle' => {
  if (report.end_time) return 'completed';
  return report.is_idle || report.status === 'idle' ? 'idle' : 'active';
};

export default function TimeReports() {
  const [reports, setReports] = useState<TimeReport[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    userId: 'all',
    projectId: 'all',
    startDate: getNDaysAgoQatar(7),
    endDate: getTodayQatar(),
    includeIdle: true
  });

  const { user, userDetails, isSuperAdmin } = useAuth();
  const organizationId = userDetails?.organization_id;
  const { toast } = useToast();

  // Memoize filter string to prevent unnecessary re-renders
  const filtersKey = useMemo(() => 
    JSON.stringify(filters), 
    [filters.userId, filters.projectId, filters.startDate, filters.endDate, filters.includeIdle]
  );

  const fetchUsers = useCallback(async () => {
    try {
      let query = supabase
        .from('users')
        .select('id, full_name, email, position')
        .order('full_name');

      // Filter by organization unless super admin
      if (organizationId && !isSuperAdmin) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setUsers((data || []) as User[]);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive',
      });
    }
  }, [toast, organizationId, isSuperAdmin]);

  const fetchProjects = useCallback(async () => {
    try {
      let query = supabase
        .from('projects')
        .select('id, name')
        .order('name');

      // Filter by organization unless super admin
      if (organizationId && !isSuperAdmin) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch projects',
        variant: 'destructive',
      });
    }
  }, [toast, organizationId, isSuperAdmin]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = startOfDayQatar(filters.startDate);
      const endDate = endOfDayQatar(filters.endDate);

      // Build query with organization filter through users relation
      // Use !inner join to filter time_logs by organization_id on users table
      let query = supabase
        .from('time_logs')
        .select(`
          *,
          users!user_id!inner(id, full_name, email, position, organization_id),
          projects!project_id(id, name)
        `)
        .lt('start_time', endDate.toISOString())
        .or(`end_time.gte.${startDate.toISOString()},end_time.is.null`);

      // Filter by organization unless super admin
      if (organizationId && !isSuperAdmin) {
        query = query.eq('users.organization_id', organizationId);
      }

      if (filters.userId && filters.userId !== 'all') {
        query = query.eq('user_id', filters.userId);
      }

      if (filters.projectId && filters.projectId !== 'all') {
        query = query.eq('project_id', filters.projectId);
      }

      if (!filters.includeIdle) {
        query = query.eq('is_idle', false);
      }

      const timeLogData = await fetchPaginated<any>(
        query.order('start_time', { ascending: false })
      );

      if (!timeLogData || timeLogData.length === 0) {
        setReports([]);
        return;
      }

      // Fetch idle_logs for the same date range to compute per-session idle time
      // (idle_seconds on time_logs is not populated; real data lives in idle_logs)
      const idleLogsData = await fetchPaginated<any>(
        supabase
          .from('idle_logs')
          .select('user_id, idle_start, idle_end, duration_seconds')
          .lt('idle_start', endDate.toISOString())
          .or(`idle_end.gte.${startDate.toISOString()},idle_end.is.null`)
          .in('user_id', [...new Set(timeLogData.map(log => log.user_id))])
          .order('idle_start')
      );

      const rangeStartMs = startDate.getTime();
      const rangeEndMs = endDate.getTime();
      const nowMs = Date.now();
      const ssData = await fetchSessionScreenshotEvidence(supabase, timeLogData, {
        rangeStartMs, rangeEndMs, nowMs,
      });
      const screenshotsBySession = getLastScreenshotByTimeLogId(timeLogData, ssData, {
        rangeStartMs, rangeEndMs, nowMs,
      });
      const reportableIntervals = getReportableTimeLogIntervals(timeLogData, {
        rangeStartMs, rangeEndMs, nowMs, screenshotsBySession,
      });
      const intervalsByLogId = new Map(reportableIntervals.map(interval => [interval.logId, interval]));

      const enrichedReports = timeLogData.map((report) => {
        const interval = intervalsByLogId.get(report.id);
        if (!interval) return null;

        const sessionSummary = calculatePayableTimeSummary([interval],
          (idleLogsData || []).filter(idle => idle.user_id === report.user_id),
          { rangeStartMs, rangeEndMs, nowMs, fallbackTimeLogs: [report] }
        );

        return {
          ...report,
          idle_seconds: sessionSummary.idleSeconds,
          smart_start_ms: interval.startMs,
          smart_end_ms: interval.endMs,
          user_name: (report as any).users?.full_name || 'Unknown User',
          user_email: (report as any).users?.email || 'Unknown',
          user_staff_id: (report as any).users?.staff_id || null,
          user_position: (report as any).users?.position || null,
          project_name: (report as any).projects?.name || 'Unknown Project'
        };
      }).filter(Boolean);

      setReports(enrichedReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch time reports',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast, organizationId, isSuperAdmin]);

  // Initial data fetch
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        await Promise.all([fetchUsers(), fetchProjects()]);
        await fetchReports();
      } catch (error) {
        console.error('Error during component initialization:', error);
      }
    };

    initializeComponent();
  }, [fetchUsers, fetchProjects, fetchReports]);

  // Re-fetch reports when filters change (but not on initial mount)
  useEffect(() => {
    if (users.length > 0 && projects.length > 0) {
      fetchReports();
    }
  }, [filtersKey, users.length, projects.length, fetchReports]);

  const calculateDuration = (start: string, end: string | null, _idleSeconds?: number, smartEndMs?: number, smartStartMs?: number): string => {
    if (!end && !smartEndMs) return 'Ongoing';

    const startMs = smartStartMs || new Date(start).getTime();
    const endMs = smartEndMs || new Date(end!).getTime();
    const diffMs = endMs - startMs;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (filters.userId && filters.userId !== 'all' && report.user_id !== filters.userId) return false;
      if (filters.projectId && filters.projectId !== 'all' && report.project_id !== filters.projectId) return false;
      if (!filters.includeIdle && report.is_idle) return false;
      return true;
    });
  }, [reports, filters.userId, filters.projectId, filters.includeIdle]);

  const activeSessionsCount = useMemo(() =>
    filteredReports.filter(report => getReportStatus(report) === 'active').length,
  [filteredReports]);

  const exportToCSV = useCallback(() => {
    try {
      const csvData = filteredReports.map((report: TimeReport) => ({
        'Employee ID': formatEmployeeId(report.user_staff_id, report.user_id),
        'User': report.user_name,
        'Email': report.user_email,
        'Position': formatPosition(report.user_position),
        'Project': report.project_name,
        'Start Time': format(new Date(report.start_time), 'yyyy-MM-dd HH:mm:ss'),
        'End Time': report.end_time ? format(new Date(report.end_time), 'yyyy-MM-dd HH:mm:ss') : 'Ongoing',
        'Duration': calculateDuration(report.start_time, report.end_time, report.idle_seconds, report.smart_end_ms, report.smart_start_ms),
        'Status': getReportStatus(report) === 'completed' ? 'Completed'
          : getReportStatus(report) === 'idle' ? 'Idle' : 'Active'
      }));

      const csvHeaders = Object.keys(csvData[0] || {});
      const csvRows = csvData.map(row => 
        csvHeaders.map(header => `"${row[header as keyof typeof row] || ''}"`).join(',')
      );
      
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Success',
        description: 'Report exported successfully',
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast({
        title: 'Error',
        description: 'Failed to export to CSV',
        variant: 'destructive',
      });
    }
  }, [filteredReports, filters.includeIdle, toast]);

  const getTotalHours = useMemo((): string => {
    // Group by user_id to merge overlapping multi-device sessions
    const byUser: { [userId: string]: TimeInterval[] } = {};
    filteredReports.forEach((report: any) => {
      const userId = report.user_id || 'unknown';
      if (!byUser[userId]) byUser[userId] = [];
      const start = report.smart_start_ms || new Date(report.start_time).getTime();
      // Use smart_end_ms if available (screenshot-capped), otherwise raw end
      const end = report.smart_end_ms || (report.end_time ? new Date(report.end_time).getTime() : Date.now());
      byUser[userId].push({ startMs: start, endMs: end });
    });

    let totalMs = 0;
    for (const intervals of Object.values(byUser)) {
      const merged = mergeTimeIntervals(intervals);
      for (const interval of merged) {
        totalMs += interval.endMs - interval.startMs;
      }
    }

    const hours = Math.floor(totalMs / (1000 * 60 * 60));
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }, [filteredReports]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Time Reports</h1>
        <Button onClick={exportToCSV} disabled={filteredReports.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">User</label>
              <EmployeeFilterCombobox
                value={filters.userId}
                onValueChange={(value) => setFilters(prev => ({ ...prev, userId: value }))}
                users={users}
                placeholder="All users"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Project</label>
              <Select 
                value={filters.projectId} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, projectId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Include Idle Time</label>
              <Select 
                value={filters.includeIdle ? 'true' : 'false'} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, includeIdle: value === 'true' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{filteredReports.length}</div>
            <div className="text-sm text-gray-500">Total Sessions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{getTotalHours}</div>
            <div className="text-sm text-gray-500">Total Time</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {activeSessionsCount}
            </div>
            <div className="text-sm text-gray-500">Active Sessions</div>
          </CardContent>
        </Card>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Time Log Details</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading reports...</div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No time logs found for the selected criteria.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report: any) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-mono text-xs">
                      {formatEmployeeId(report.user_staff_id, report.user_id)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{report.user_name}</div>
                        <div className="text-sm text-gray-500">{report.user_email}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatPosition(report.user_position)}
                    </TableCell>
                    <TableCell>{report.project_name}</TableCell>
                    <TableCell>
                      {format(new Date(report.start_time), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {report.end_time 
                        ? format(new Date(report.end_time), 'MMM d, yyyy HH:mm')
                        : 'Ongoing'
                      }
                    </TableCell>
                    <TableCell>
                      {calculateDuration(report.start_time, report.end_time, report.idle_seconds, report.smart_end_ms, report.smart_start_ms)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getReportStatus(report) === 'active' ? 'default' : 'secondary'}>
                        {getReportStatus(report) === 'completed' ? 'Completed'
                          : getReportStatus(report) === 'idle' ? 'Idle' : 'Active'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
